/**
 * Explicit, type-aware comparators for the sortable tables.
 *
 * TanStack's automatic sorting samples a column's values after the tenth row
 * and falls back to a string comparison, which orders numbers and nulls wrong
 * on a short list or a column whose later rows are all null. So every sortable
 * column names its comparator here instead.
 *
 * Each comparator orders for ASCENDING and sends null/undefined to the end. In
 * the table, columns pair these with `sortUndefined: 'last'` (and an accessor
 * that maps null to undefined) so nulls stay last under descending too —
 * TanStack applies `sortUndefined` before it inverts for descending, which a
 * comparator alone cannot do. `orderValues` below mirrors that exact behavior
 * for the unit tests.
 */

type Nullable<T> = T | null | undefined;

function isBlank(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/** Wraps a comparator of defined values so null/undefined always sort last. */
function nullsLast<T>(a: Nullable<T>, b: Nullable<T>, cmp: (a: T, b: T) => number): number {
  const an = isBlank(a);
  const bn = isBlank(b);
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  return cmp(a, b);
}

/** Case- and accent-insensitive text order. */
export function compareText(a: Nullable<string>, b: Nullable<string>): number {
  return nullsLast(a, b, (x, y) => x.localeCompare(y, undefined, { sensitivity: 'accent' }));
}

/** ISO date strings (YYYY-MM-DD…) order correctly as plain strings. */
export function compareIsoDate(a: Nullable<string>, b: Nullable<string>): number {
  return nullsLast(a, b, (x, y) => (x < y ? -1 : x > y ? 1 : 0));
}

/** Integer order. */
export function compareInteger(a: Nullable<number>, b: Nullable<number>): number {
  return nullsLast(a, b, (x, y) => (x < y ? -1 : x > y ? 1 : 0));
}

interface Decimal {
  neg: boolean;
  /** Integer digits, leading zeros stripped (never empty; "0" for zero). */
  int: string;
  /** Fraction digits, trailing zeros stripped (may be empty). */
  frac: string;
}

function parseDecimal(value: string): Decimal {
  const match = /^\s*(-)?(\d+)(?:\.(\d+))?\s*$/.exec(value);
  if (!match) return { neg: false, int: '0', frac: '' };
  const int = (match[2] ?? '0').replace(/^0+(?=\d)/, '');
  const frac = (match[3] ?? '').replace(/0+$/, '');
  const neg = match[1] === '-' && !(int === '0' && frac === '');
  return { neg, int, frac };
}

/** Compare two non-negative magnitudes digit by digit — no float math. */
function compareMagnitude(a: Decimal, b: Decimal): number {
  if (a.int.length !== b.int.length) return a.int.length < b.int.length ? -1 : 1;
  if (a.int !== b.int) return a.int < b.int ? -1 : 1;
  const width = Math.max(a.frac.length, b.frac.length);
  const af = a.frac.padEnd(width, '0');
  const bf = b.frac.padEnd(width, '0');
  return af < bf ? -1 : af > bf ? 1 : 0;
}

/**
 * Numeric-string order without ever calling Number(): money and ratios arrive
 * as strings and "12.00" must beat "9.50". Compares sign, then integer length,
 * then the digits.
 */
export function compareNumericString(a: Nullable<string>, b: Nullable<string>): number {
  return nullsLast(a, b, (x, y) => {
    const da = parseDecimal(x);
    const db = parseDecimal(y);
    if (da.neg !== db.neg) return da.neg ? -1 : 1;
    const magnitude = compareMagnitude(da, db);
    return da.neg ? -magnitude : magnitude;
  });
}

export type Comparator<T> = (a: Nullable<T>, b: Nullable<T>) => number;

/**
 * Orders values the way the table does: null/undefined always last, whichever
 * the direction, and the rest by `cmp` (reversed for descending). Used by the
 * tests to pin the behavior the columns rely on.
 */
export function orderValues<T>(
  values: readonly Nullable<T>[],
  cmp: Comparator<T>,
  desc = false,
): Nullable<T>[] {
  return [...values].sort((a, b) => {
    const an = isBlank(a);
    const bn = isBlank(b);
    if (an || bn) return an && bn ? 0 : an ? 1 : -1;
    const result = cmp(a, b);
    return desc ? -result : result;
  });
}

/**
 * Turns a value comparator into a TanStack `sortingFn`. Pair the column with
 * `sortUndefined: 'last'` and an accessor that maps null to undefined so the
 * comparator only ever sees defined values inside the table.
 */
export function sortingFnFor<T>(cmp: Comparator<T>) {
  return (
    rowA: { getValue: (id: string) => unknown },
    rowB: { getValue: (id: string) => unknown },
    columnId: string,
  ): number => cmp(rowA.getValue(columnId) as Nullable<T>, rowB.getValue(columnId) as Nullable<T>);
}
